import type { Store } from '@blocksuite/affine/store';
import {
  DATABASE_ROW_SHARD_ROWS_MAP_KEY,
  parseServerDatabaseRowShardDocId,
} from '@streamlain/database-identity';
import { Array as YArray, Map as YMap } from 'yjs';
export type SnapshotExportArchiveEntry = {
  readonly sourceDocId: string;
  readonly store: Store;
};

export type BlobColumnLookup = ReadonlyMap<
  string,
  {
    readonly attachmentColumnIds: ReadonlySet<string>;
    readonly imageColumnIds: ReadonlySet<string>;
  }
>;

export function collectSnapshotExportBlobIds(
  pages: readonly Store[],
  rootDocIds: readonly string[],
  rowShardDocIds: readonly string[]
): string[] {
  const entries = collectSnapshotExportArchiveEntries(
    pages,
    rootDocIds,
    rowShardDocIds
  );
  const blobIds = new Set<string>();
  const blobColumnLookup = collectBlobColumnLookup(entries);

  for (const entry of entries) {
    const blocks = entry.store.spaceDoc.getMap('blocks') as YMap<unknown>;
    blocks.forEach(block => {
      if (!(block instanceof YMap)) return;

      addBlobId(blobIds, block.get('prop:sourceId'));
    });

    const rowShard = parseServerDatabaseRowShardDocId(
      entry.sourceDocId,
      entry.store.workspace.id
    );
    if (!rowShard) continue;

    const columns = blobColumnLookup.get(rowShard.databaseId);
    if (!columns) continue;

    collectDatabaseCellBlobIds(
      blobIds,
      entry.store.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY),
      columns
    );
  }

  return [...blobIds].sort();
}

export function collectSnapshotExportArchiveEntries(
  pages: readonly Store[],
  rootDocIds: readonly string[],
  rowShardDocIds: readonly string[]
): SnapshotExportArchiveEntry[] {
  const entries = new Map<string, SnapshotExportArchiveEntry>();
  for (const page of pages) {
    entries.set(page.id, { sourceDocId: page.id, store: page });
  }

  const workspace = pages[0]?.workspace;
  if (!workspace) {
    return [...entries.values()];
  }

  for (const docId of [...rootDocIds, ...rowShardDocIds]) {
    const store = workspace.getDoc(docId)?.getStore({ id: docId });
    if (store) {
      entries.set(docId, { sourceDocId: docId, store });
    }
  }

  return [...entries.values()];
}

export function readYValue(value: unknown): unknown {
  if (value instanceof YMap) {
    return Object.fromEntries(
      Array.from(value.entries()).map(([key, item]) => [key, readYValue(item)])
    );
  }

  if (value instanceof YArray) {
    return value.toArray().map(readYValue);
  }

  if (Array.isArray(value)) {
    return value.map(readYValue);
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [
        key,
        readYValue(item),
      ])
    );
  }

  return value;
}

export function addBlobId(blobIds: Set<string>, value: unknown): void {
  if (typeof value !== 'string') return;
  const blobId = value.replace(/^\//, '');
  if (blobId) {
    blobIds.add(blobId);
  }
}

export function collectBlobIdsFromDatabaseCell(
  blobIds: Set<string>,
  columnType: string,
  cell: unknown
): void {
  const value =
    cell && typeof cell === 'object' && !Array.isArray(cell) && 'value' in cell
      ? (cell as { value?: unknown }).value
      : cell;

  if (columnType === 'image') {
    addBlobId(blobIds, value);
    return;
  }

  if (
    columnType === 'attachment' &&
    value &&
    typeof value === 'object' &&
    !Array.isArray(value)
  ) {
    Object.keys(value as Record<string, unknown>).forEach(blobId =>
      addBlobId(blobIds, blobId)
    );
  }
}

export function readDatabaseBlobColumnIds(block: YMap<unknown>) {
  const attachmentColumnIds = new Set<string>();
  const imageColumnIds = new Set<string>();
  const columns = readYValue(block.get('prop:columns'));

  if (Array.isArray(columns)) {
    columns.forEach(column => {
      if (!column || typeof column !== 'object' || Array.isArray(column)) {
        return;
      }
      const { id, type } = column as { id?: unknown; type?: unknown };
      if (typeof id !== 'string' || typeof type !== 'string') return;

      if (type === 'attachment') {
        attachmentColumnIds.add(id);
      } else if (type === 'image') {
        imageColumnIds.add(id);
      }
    });
  }

  return { attachmentColumnIds, imageColumnIds };
}

export function collectDatabaseCellBlobIds(
  blobIds: Set<string>,
  cells: unknown,
  columns: {
    readonly attachmentColumnIds: ReadonlySet<string>;
    readonly imageColumnIds: ReadonlySet<string>;
  }
): void {
  const rows = readYValue(cells);
  if (!rows || typeof rows !== 'object' || Array.isArray(rows)) return;

  Object.values(rows as Record<string, unknown>).forEach(row => {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return;

    const rowCells = row as Record<string, unknown>;
    columns.attachmentColumnIds.forEach(columnId =>
      collectBlobIdsFromDatabaseCell(blobIds, 'attachment', rowCells[columnId])
    );
    columns.imageColumnIds.forEach(columnId =>
      collectBlobIdsFromDatabaseCell(blobIds, 'image', rowCells[columnId])
    );
  });
}

export function collectBlobColumnLookup(
  entries: readonly SnapshotExportArchiveEntry[]
): BlobColumnLookup {
  const lookup = new Map<
    string,
    {
      readonly attachmentColumnIds: ReadonlySet<string>;
      readonly imageColumnIds: ReadonlySet<string>;
    }
  >();

  entries.forEach(entry => {
    const blocks = entry.store.spaceDoc.getMap('blocks') as YMap<unknown>;
    blocks.forEach((block, blockId) => {
      if (
        !(block instanceof YMap) ||
        block.get('sys:flavour') !== 'affine:database'
      ) {
        return;
      }

      collectDatabaseBlockColumnLookup(
        lookup,
        block,
        readDatabaseBlobColumnIds(block),
        blockId
      );
    });
  });

  return lookup;
}

export function collectDatabaseBlockColumnLookup(
  lookup: Map<
    string,
    {
      readonly attachmentColumnIds: ReadonlySet<string>;
      readonly imageColumnIds: ReadonlySet<string>;
    }
  >,
  block: YMap<unknown>,
  columns: {
    readonly attachmentColumnIds: ReadonlySet<string>;
    readonly imageColumnIds: ReadonlySet<string>;
  },
  blockId?: string
): void {
  if (blockId) {
    lookup.set(blockId, columns);
  }

  const databaseId = block.get('prop:databaseId');
  if (typeof databaseId === 'string' && databaseId) {
    lookup.set(databaseId, columns);
  }
}
